/**
 * use2FALogin
 *
 * Manages the TOTP challenge step during login.
 * Called only when the initial /auth/login returned { requires2FA: true }.
 * The pendingToken is read from Zustand (set by useAuth.login).
 *
 * On success: calls setAuth → redirects to practice (or original destination).
 * On cancel:  clears pending token → back to login page.
 */

import { useState, useCallback, type FormEvent, type ChangeEvent } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuthStore } from '../store/useAuthStore';
import { api2FALogin } from '../api/auth';
import { ApiError } from '../api/client';

export interface Use2FALoginReturn {
  code: string;
  error: string | null;
  loading: boolean;
  handleChange: (e: ChangeEvent<HTMLInputElement>) => void;
  handleSubmit: (e: FormEvent) => void;
  handleCancel: () => void;
}

export function use2FALogin(): Use2FALoginReturn {
  const pendingToken    = useAuthStore((s) => s.pendingTwoFAToken);
  const setAuth         = useAuthStore((s) => s.setAuth);
  const clearPending    = useAuthStore((s) => s.clearPendingTwoFA);
  const navigate        = useNavigate();
  const location        = useLocation();

  const redirectTo =
    (location.state as { from?: { pathname: string } } | null)?.from?.pathname ?? '/practice';

  const [code,    setCode]    = useState('');
  const [error,   setError]   = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const handleChange = useCallback((e: ChangeEvent<HTMLInputElement>) => {
    // Only digits, max 6 characters
    const val = e.target.value.replace(/\D/g, '').slice(0, 6);
    setCode(val);
    setError(null);
  }, []);

  const handleSubmit = useCallback((e: FormEvent) => {
    e.preventDefault();

    if (!pendingToken) {
      navigate('/login', { replace: true });
      return;
    }

    if (code.length !== 6) {
      setError('Enter the 6-digit code from your authenticator app');
      return;
    }

    setError(null);
    setLoading(true);

    api2FALogin({ pendingToken, code })
      .then((res) => {
        if (res.requires2FA) return; // should never happen here
        clearPending();
        setAuth(res.user, res.accessToken);
        navigate(redirectTo, { replace: true });
      })
      .catch((err: unknown) => {
        if (err instanceof ApiError) {
          if (err.status === 401) {
            setError('Incorrect code — check your authenticator app and try again');
          } else if (err.status === 429) {
            setError('Too many attempts — please wait a moment');
          } else {
            setError('Verification failed — please try again');
          }
        } else {
          setError('Network error — check your connection');
        }
        setLoading(false);
      });
    // No finally setLoading(false) — on success the component unmounts
  }, [code, pendingToken, setAuth, clearPending, navigate, redirectTo]);

  const handleCancel = useCallback(() => {
    clearPending();
    navigate('/login', { replace: true });
  }, [clearPending, navigate]);

  return { code, error, loading, handleChange, handleSubmit, handleCancel };
}
